import {
    INDEX_EXCHANGE,
    MEDIA_NET_BANNER,
    MEDIA_NET_VIDEO,
    ONETAG,
    OMNIDEX_BANNER,
    PUBMATIC_BANNER,
    OMNIDEX_VIDEO,
    PUBMATIC_VIDEO,
    PLAYGAMA,
    PLAYGAMA_FALLBACK,
    TAPPX_BANNER,
    TAPPX_VIDEO,
    LOOPME,
    GENIEE,
    ADITUDE_DISPLAY,
    ADITUDE_VIDEO,
    EQUATIV,
    APS,
    INMOBI_BANNER,
    INMOBI_VIDEO,
} from '@/modules/adv/header-bidding/prebid/bidders-config'
import type { PlatformType } from '@/types'

import { DEFAULT_AD_UNIT_TIMEOUT, PAGE_AD_TIMEOUT } from './constants'
import {
    createGoogleAdSettings,
    createGoogleBannerSettings,
    createGoogleInstreamSettings,
    createHeaderBiddingAdSettings,
} from './provider-factories'

const STATIC_GOOGLE_AD_UNITS: Record<string, string> = {
    sticky_mobile_non_game_page: 'sticky_mobile',
    sticky_mobile: 'sticky_mobile',
    sticky_tablet: 'sticky_mobile',
    sticky_desktop: 'sticky_desktop',
    sidebar: 'sidebar',
    sidebar_bottom: 'sidebar_bottom',
    sidebar_fullscreen: 'sidebar_fullscreen',
    sidebar_bottom_fullscreen: 'sidebar_bottom_fullscreen',
    leaderboard: 'leaderboard',
    leaderboard_rest: 'leaderboard_rest',
    widget_sidebar: 'widget_sidebar',
    widget_sidebar_bottom: 'widget_sidebar_bottom',
    widget_horizontal: 'widget_horizontal',
    widget_horizontal_2: 'widget_horizontal_2',
    widget_sticky_mobile: 'widget_sticky_mobile',
    widget_sticky_tablet: 'widget_sticky_mobile',
    universal_banner_1: 'universal_banner_1',
    universal_banner_short_1: 'universal_banner_1',
    universal_banner_2: 'universal_banner_2',
}

export const getCommonProvidersAdSettings = (
    gamAccount: string,
    platform: PlatformType,
    isPlaygamaBidderOnly?: boolean,
) => {
    const COMMON_BIDDERS_FOR_GOOGLE = isPlaygamaBidderOnly
        ? [PLAYGAMA]
        : [
              ONETAG,
              TAPPX_BANNER,
              MEDIA_NET_BANNER,
              PLAYGAMA,
              GENIEE,
              INMOBI_BANNER,
              OMNIDEX_BANNER,
              PUBMATIC_BANNER,
              LOOPME,
              ADITUDE_DISPLAY,
              EQUATIV,
              INDEX_EXCHANGE,
              APS,
          ]

    return {
        HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS: createHeaderBiddingAdSettings(
            platform,
            isPlaygamaBidderOnly
                ? [PLAYGAMA_FALLBACK]
                : [
                      TAPPX_BANNER,
                      MEDIA_NET_BANNER,
                      ONETAG,
                      GENIEE,
                      ADITUDE_DISPLAY,
                      OMNIDEX_BANNER,
                      PUBMATIC_BANNER,
                      LOOPME,
                      INMOBI_BANNER,
                      EQUATIV,
                      INDEX_EXCHANGE,
                      APS,
                      PLAYGAMA_FALLBACK,
                  ],
        ),
        GOOGLE_AD_PORTAL_SETTINGS: createGoogleAdSettings(gamAccount, platform, {
            timeout: PAGE_AD_TIMEOUT,
            adUnitConfig: {
                desktop: STATIC_GOOGLE_AD_UNITS,
                mobile: STATIC_GOOGLE_AD_UNITS,
            },
            headerBidding: {
                bidders: COMMON_BIDDERS_FOR_GOOGLE,
            },
            apstag: !isPlaygamaBidderOnly,
        }),
        GOOGLE_AD_GAME_PAGE_SETTINGS_REWARDED_FORMAT: createGoogleAdSettings(gamAccount, platform, {
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            forcedOutOfPageAdFormat: 'rewarded',
            adUnitConfig: {
                desktop: {
                    rewarded: 'rewarded_desktop',
                    interstitial_preroll: 'h5_interstitial_preroll_desktop',
                    interstitial: 'h5_interstitial_desktop',
                },
                mobile: {
                    rewarded: 'rewarded_mobile',
                    interstitial_preroll: 'h5_interstitial_preroll_mobile',
                    interstitial: 'h5_interstitial_mobile',
                },
            },
        }),
        GOOGLE_AD_GAME_PAGE_SETTINGS_INTERSTITIAL_FORMAT: createGoogleAdSettings(gamAccount, platform, {
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            forcedOutOfPageAdFormat: 'interstitial',
            adUnitConfig: {
                desktop: {
                    rewarded: 'rewarded_2_desktop',
                    interstitial_preroll: 'h5_interstitial_preroll_2_desktop',
                    interstitial: 'h5_interstitial_2_desktop',
                },
                mobile: {
                    rewarded: 'rewarded_2_mobile',
                    interstitial_preroll: 'h5_interstitial_preroll_2_mobile',
                    interstitial: 'h5_interstitial_2_mobile',
                },
            },
            headerBidding: {
                bidders: isPlaygamaBidderOnly
                    ? [PLAYGAMA]
                    : [
                          PLAYGAMA,
                          ADITUDE_DISPLAY,
                          EQUATIV,
                          INDEX_EXCHANGE,
                          INMOBI_BANNER,
                          OMNIDEX_BANNER,
                          PUBMATIC_BANNER,
                          MEDIA_NET_BANNER,
                          APS,
                      ],
            },
            apstag: !isPlaygamaBidderOnly,
        }),
        GOOGLE_BANNER_GAME_PAGE_SETTINGS: createGoogleBannerSettings(gamAccount, platform, {
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            headerBidding: {
                bidders: COMMON_BIDDERS_FOR_GOOGLE,
            },
            apstag: !isPlaygamaBidderOnly,
            adUnitConfig: {
                desktop: {
                    interstitial_preroll: 'banner_ingame_preroll_desktop',
                    interstitial: 'banner_ingame_desktop',
                    rewarded: 'banner_ingame_rewarded_desktop',
                },
                mobile: {
                    interstitial_preroll: 'banner_ingame_preroll_mobile',
                    interstitial: 'banner_ingame_mobile',
                    rewarded: 'banner_ingame_rewarded_mobile',
                },
            },
        }),
        GOOGLE_INSTREAM_GAME_PAGE_SETTINGS: createGoogleInstreamSettings(gamAccount, platform, {
            adUnitConfig: {
                desktop: {
                    rewarded: 'instream_rewarded_desktop',
                    interstitial: 'instream_desktop',
                    interstitial_preroll: 'instream_preroll_desktop',
                },
                mobile: {
                    rewarded: 'instream_rewarded_mobile',
                    interstitial: 'instream_mobile',
                    interstitial_preroll: 'instream_preroll_mobile',
                },
            },
            headerBidding: {
                bidders: isPlaygamaBidderOnly
                    ? [PLAYGAMA]
                    : [
                          MEDIA_NET_VIDEO,
                          PLAYGAMA,
                          INMOBI_VIDEO,
                          OMNIDEX_VIDEO,
                          PUBMATIC_VIDEO,
                          LOOPME,
                          ADITUDE_VIDEO,
                          EQUATIV,
                          INDEX_EXCHANGE,
                          TAPPX_VIDEO,
                          ONETAG,
                      ],
            },
        }),
    }
}
